import { ParseClothImageResult, WarpParsedImageResult } from "@/backend/base";
import { LeftPanelItemType, isTryOnLeftPanelItemType } from "@/components/constants/editor-options";
import {
  TryOnClothBrushStrokesHistory,
  TryOnClothCanvasController,
} from "@/components/editor/tryon-cloth-canvas-controller";
import {
  TryOnEditorController,
  TryOnEditorControllerProps,
} from "@/components/editor/tryon-editor-controller";
import {
  TryOnPersonCanvasController,
  TryOnPersonCanvasHistory,
} from "@/components/editor/tryon-person-canvas-controller";
import { displayUiMessage } from "@/components/utils/display-message";
import { cleanupPrompt } from "@/components/utils/prompt";
import {
  StateUpdater,
  TryOnParsedClothImageBbox,
  TryOnRenderResults,
  UserProjectType,
} from "@/core/common/types";
import { getHtmlImageElementFromUrlAsync } from "@/core/utils/image-utils";
import React from "react";
import { editorContextStore } from "./editor-context";

export function useTryOnClothImageElement(): [
  HTMLImageElement | undefined,
  (value: StateUpdater<HTMLImageElement | undefined>) => void,
] {
  return [
    editorContextStore((state) => state.tryOnClothImageElement),
    editorContextStore((state) => state.setTryOnClothImageElement),
  ];
}

export function useTryOnWarpedClothImageElement(): [
  HTMLImageElement | undefined,
  (value: StateUpdater<HTMLImageElement | undefined>) => void,
] {
  return [
    editorContextStore((state) => state.tryOnWarpedClothImageElement),
    editorContextStore((state) => state.setTryOnWarpedClothImageElement),
  ];
}

export function useTryOnWarpedHumanMaskImageElement(): [
  HTMLImageElement | undefined,
  (value: StateUpdater<HTMLImageElement | undefined>) => void,
] {
  return [
    editorContextStore((state) => state.tryOnWarpedHumanMaskImageElement),
    editorContextStore((state) => state.setTryOnWarpedHumanMaskImageElement),
  ];
}

export function useTryOnPersonImageElement(): [
  HTMLImageElement | undefined,
  (value: StateUpdater<HTMLImageElement | undefined>) => void,
] {
  return [
    editorContextStore((state) => state.tryOnPersonImageElement),
    editorContextStore((state) => state.setTryOnPersonImageElement),
  ];
}

export function useTryOnParsedClothImageElement(): [
  HTMLImageElement | undefined,
  (value: StateUpdater<HTMLImageElement | undefined>) => void,
] {
  return [
    editorContextStore((state) => state.tryOnParsedClothImageElement),
    editorContextStore((state) => state.setTryOnParsedClothImageElement),
  ];
}

export function useTryOnParsedClothImageBbox(): [
  TryOnParsedClothImageBbox | undefined,
  (value: StateUpdater<TryOnParsedClothImageBbox | undefined>) => void,
] {
  return editorContextStore((state) => [
    state.tryOnParsedClothImageBbox,
    state.setTryOnParsedClothImageBbox,
  ]);
}

export function useTryOnRenderResults(): [
  TryOnRenderResults,
  (value: StateUpdater<TryOnRenderResults>) => void,
] {
  return editorContextStore((state) => [state.tryOnRenderResults, state.setTryOnRenderResults]);
}

export function getTryOnPersonImageElementRootUrl(imageId: string) {
  return `https://flair.ai/cdn-cgi/imagedelivery/i1XPW6iC_chU01_6tBPo8Q/model-preview-${imageId}`;
}

export function getTryOnPersonImageElementImageUrl(imageId: string) {
  const rootUrl = getTryOnPersonImageElementRootUrl(imageId);
  return `${rootUrl}/public`;
}

export function getTryOnPersonImageElementPreviewUrl(imageId: string) {
  const rootUrl = getTryOnPersonImageElementRootUrl(imageId);
  return `${rootUrl}/256`;
}

export function setTryOnPersonImageElementFromImageId(imageId: string, imageUrl?: string) {
  const { tryOnPersonImageElement, setTryOnModelId, setTryOnPersonImageElement } =
    editorContextStore.getState();

  setTryOnModelId(imageId);

  imageUrl = imageUrl || getTryOnPersonImageElementImageUrl(imageId);

  if (!tryOnPersonImageElement || tryOnPersonImageElement.src !== imageUrl) {
    getHtmlImageElementFromUrlAsync(imageUrl).then(setTryOnPersonImageElement);
  }
}

export async function generateClothPrompt() {
  const {
    backend,
    tryOnClothImageElement,
    setTryOnClothPrompt,
    tryOnClothPromptState,
    setTryOnClothPromptState,
  } = editorContextStore.getState();

  if (!backend || !tryOnClothImageElement) {
    return;
  }

  if (tryOnClothPromptState !== "idle") {
    return;
  }

  const imageUrl = tryOnClothImageElement.src;

  if (!imageUrl) {
    return;
  }

  setTryOnClothPromptState("generating");

  return backend
    .getImageCaption({
      imageUrl,
    })
    .then((caption) => {
      if (!caption) {
        return;
      }

      const currentTryOnClothPromptState = editorContextStore.getState().tryOnClothPromptState;

      if (currentTryOnClothPromptState === "idle") {
        // The prompt state is reset. This is likely due to the change in cloth element. Therefore, we should not apply the auto-generated prompt.
        return;
      }

      setTryOnClothPrompt((prevCaption) => prevCaption || cleanupPrompt(caption));

      setTryOnClothPromptState("idle");

      return caption;
    });
}

export function handleClothImageUpdate() {
  const {
    setTryOnClothPrompt,
    setTryOnClothPromptState,
    setTryOnParsedClothImageBbox,
    setTryOnParsedClothImageElement,
    setTryOnWarpedHumanMaskImageElement,
    setTryOnParsedClothUpdated,
  } = editorContextStore.getState();
  setTryOnParsedClothUpdated(false);
  setTryOnParsedClothImageBbox(undefined);
  setTryOnParsedClothImageElement(undefined);
  setTryOnWarpedHumanMaskImageElement(undefined);
  setTryOnClothPrompt("");
  setTryOnClothPromptState("idle");
}

function setTryOnParsedClothImageElement(image_parsed: string) {
  return getHtmlImageElementFromUrlAsync(image_parsed).then((element) => {
    editorContextStore.getState().setTryOnParsedClothImageElement((prevElement) => {
      prevElement?.remove();
      return element;
    });
  });
}

function setTryOnWarpedClothImageElement(warped_cloth: string) {
  return getHtmlImageElementFromUrlAsync(warped_cloth).then((element) => {
    editorContextStore.getState().setTryOnWarpedClothImageElement((prevElement) => {
      prevElement?.remove();
      return element;
    });
  });
}

function setTryOnWarpedHumanMaskImageElement(warped_human_mask: string) {
  return getHtmlImageElementFromUrlAsync(warped_human_mask).then((element) => {
    editorContextStore.getState().setTryOnWarpedHumanMaskImageElement((prevElement) => {
      prevElement?.remove();
      return element;
    });
  });
}

export function handleWarpParsedClothImageResult(warpedResult: Partial<WarpParsedImageResult>) {
  if (!warpedResult) {
    return Promise.resolve([]);
  }

  const imageLoadingPromises: Promise<any>[] = [];

  if (warpedResult.warped_cloth) {
    imageLoadingPromises.push(setTryOnWarpedClothImageElement(warpedResult.warped_cloth));
  }

  if (warpedResult.warped_human_mask) {
    imageLoadingPromises.push(setTryOnWarpedHumanMaskImageElement(warpedResult.warped_human_mask));
  }

  return Promise.all(imageLoadingPromises);
}

export function handleParsedClothImageResult(result: Partial<ParseClothImageResult>) {
  if (!result) {
    displayUiMessage("Cannot fit the cloth onto the model.");

    return Promise.resolve([]);
  }

  const imageLoadingPromises: Promise<any>[] = [];
  const { bbox, image_parsed, warped_cloth, warped_human_mask } = result;

  editorContextStore.getState().setTryOnParsedClothImageBbox(bbox);

  if (image_parsed) {
    imageLoadingPromises.push(setTryOnParsedClothImageElement(image_parsed));
  }

  if (warped_cloth) {
    imageLoadingPromises.push(setTryOnWarpedClothImageElement(warped_cloth));
  }

  if (warped_human_mask) {
    imageLoadingPromises.push(setTryOnWarpedHumanMaskImageElement(warped_human_mask));
  }

  return Promise.all(imageLoadingPromises);
}

type ITryOnClothEditorContext = {
  tryOnClothBrushStrokesHistoryRef: {
    current?: TryOnClothBrushStrokesHistory;
  };
  tryOnClothCanvasControllerRef: {
    current?: TryOnClothCanvasController;
  };
  tryOnPersonCanvasHistoryRef: {
    current?: TryOnPersonCanvasHistory;
  };
  tryOnPersonCanvasControllerRef: {
    current?: TryOnPersonCanvasController;
  };
};

export function useTryOnClothBrushStrokesHistoryRef() {
  return editorContextStore(
    (state) =>
      state.tryOnEditorController?.tryOnClothBrushStrokesHistoryRef || {
        current: undefined,
      },
  );
}

export function useTryOnClothCanvasController() {
  return editorContextStore(
    (state) =>
      state.tryOnEditorController?.tryOnClothCanvasControllerRef || {
        current: undefined,
      },
  );
}

export function useTryOnPersonCanvasController() {
  return editorContextStore(
    (state) =>
      state.tryOnEditorController?.tryOnPersonCanvasControllerRef || {
        current: undefined,
      },
  );
}

export function useTryOnPersonHistoryRef() {
  return editorContextStore(
    (state) =>
      state.tryOnEditorController?.tryOnPersonCanvasHistoryRef || {
        current: undefined,
      },
  );
}

export const TryOnEditorContextProvider = React.memo(function TryOnEditorContextProvider({
  children,
  bounds,
}: TryOnEditorControllerProps & {
  children: React.ReactNode;
}) {
  React.useEffect(() => {
    const controller = new TryOnEditorController({
      bounds,
    });
    editorContextStore.getState().setTryOnEditorController(controller);
    return () => {
      controller.destroy();
      editorContextStore.getState().setTryOnEditorController((prevController) => {
        if (prevController === controller) {
          return undefined;
        }
        return prevController;
      });
    };
  }, [bounds]);

  return <>{children}</>;
});

export function resetTryOnEditor() {
  const {
    setTryOnClothPrompt,
    setTryOnRenderProgress,
    setTryOnClothImageElement,
    setTryOnRenderResults,
    setTryOnActiveClothMaskType,
    setTryOnClothMaskPaintState,
    setTryOnClothPromptState,
    setTryOnEditorState,
    setTryOnPersonImageElement,
    setTryOnPersonPaintState,
    setTryOnParsedClothImageBbox,
    setTryOnParsedClothImageElement,
    setTryOnWarpedHumanMaskImageElement,
    setTryOnWarpedClothImageElement,
  } = editorContextStore.getState();

  setTryOnClothPrompt("");
  setTryOnRenderProgress(0);
  setTryOnClothImageElement(undefined);
  setTryOnRenderResults([]);
  setTryOnActiveClothMaskType("empty");
  setTryOnClothMaskPaintState("idle");
  setTryOnClothPromptState("idle");
  setTryOnEditorState("idle");
  setTryOnPersonImageElement(undefined);
  setTryOnPersonPaintState("idle");
  setTryOnParsedClothImageBbox(undefined);
  setTryOnParsedClothImageElement(undefined);
  setTryOnWarpedHumanMaskImageElement(undefined);
  setTryOnWarpedClothImageElement(undefined);
}

function isTryOnEditorVisible(
  activeLeftPanels: LeftPanelItemType[],
  projectType?: UserProjectType,
) {
  if (projectType !== UserProjectType.TryOn) {
    return false;
  }

  if (!activeLeftPanels?.length) {
    return false;
  }

  const activeLeftPanel = activeLeftPanels[activeLeftPanels.length - 1];

  return isTryOnLeftPanelItemType(activeLeftPanel);
}

export function useTryOnEditorVisible() {
  const projectType = editorContextStore((state) => state.projectType);

  const activeLeftPanels = editorContextStore((state) => state.activeLeftPanels);

  const [visible, setVisible] = React.useState(isTryOnEditorVisible(activeLeftPanels, projectType));

  React.useEffect(() => {
    setVisible(isTryOnEditorVisible(activeLeftPanels, projectType));
  }, [activeLeftPanels, projectType]);

  return visible;
}
